/**
 * Cart "Your order will ship by [Date]" text.
 *
 * Mirrors the PDP shipping-date logic against window.scs_off_site_skus_full,
 * but resolves the SKU(s) the same way availability checks do: prefer the
 * bundle's component SKUs (_components), falling back to the single part
 * number (_part_no / _new_sku) when no component matches.
 */ 
(function () {
  'use strict';

  // Fallback estimate when no shipping range is found for a SKU — we'd
  // rather show an estimate than hide the ship-by text. Mirrors
  // DEFAULT_SHIP_BY_DAYS in scs-luxury-seat-covers/src/utils/shippingDate.ts.
  var DEFAULT_SHIP_BY_DAYS = 2;

  // New gating for this A/B test's resurfaced run: only show ship-by text
  // when the worst-case shipping speed is fast (< 3 business days). 3+
  // hides it entirely — the old test always showed it regardless of speed.
  var MAX_FAST_SHIPPING_BUSINESS_DAYS = 3;

  var MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  function pad2(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function toISODate(date) {
    return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate());
  }

  // Non-working days beyond weekends, fetched from
  // GET https://scsbiz3.wearevisum.com/api/v1/public/holidays — mirrors
  // TagShipTime::addBusinessDays in scsbiz3.wearevisum.com and
  // addBusinessDays() in scs-luxury-seat-covers/src/utils/shippingDate.ts,
  // which are the sources of truth for this logic.
  var HOLIDAYS_API_URL = 'https://scsbiz3.wearevisum.com/api/v1/public/holidays';
  var HOLIDAYS_STORAGE_KEY = 'scs_holidays_cache_v1';
  var HOLIDAYS_TTL_MS = 60 * 60 * 1000; // 1 hour — matches the API's own server-side cache
  var cachedHolidays = [];

  function loadHolidaysFromStorage() {
    try {
      var raw = window.localStorage.getItem(HOLIDAYS_STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.dates)) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function saveHolidaysToStorage(dates) {
    try {
      window.localStorage.setItem(HOLIDAYS_STORAGE_KEY, JSON.stringify({ dates: dates, fetchedAt: Date.now() }));
    } catch (e) {
      // localStorage unavailable/full — best effort only, cachedHolidays still holds the data in-memory.
    }
  }

  function fetchHolidays() {
    fetch(HOLIDAYS_API_URL, { mode: 'cors' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        if (!json || !Array.isArray(json.data)) return;
        var dates = json.data.map(function (h) { return h && h.date; }).filter(Boolean);
        cachedHolidays = dates;
        saveHolidaysToStorage(dates);
        // Re-render any already-computed ship-by text now that holidays are known.
        scheduleUpdate();
      })
      .catch(function () {
        // Network hiccup — keep using whatever's cached (storage or weekends-only).
      });
  }

  (function initHolidays() {
    var stored = loadHolidaysFromStorage();
    if (stored) {
      cachedHolidays = stored.dates;
    }
    if (!stored || (Date.now() - stored.fetchedAt) > HOLIDAYS_TTL_MS) {
      fetchHolidays();
    }
  })();

  function addBusinessDays(from, days, holidays) {
    var holidaySet = {};
    (holidays || cachedHolidays).forEach(function (d) { holidaySet[d] = true; });
    var result = new Date(from.getTime());
    var added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      var dow = result.getDay();
      if (dow !== 0 && dow !== 6 && !holidaySet[toISODate(result)]) {
        added += 1;
      }
    }
    return result;
  }

  function ordinal(n) {
    if (n >= 11 && n <= 13) return 'th';
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  function formatShipByDate(date) {
    return MONTH_NAMES[date.getMonth()] + ' ' + date.getDate() + ordinal(date.getDate());
  }

  function parseShippingRangeMax(range) {
    if (!range) return null;
    var parts = String(range).split('-')
      .map(function (s) { return parseInt(s.trim(), 10); })
      .filter(function (n) { return !isNaN(n); });
    if (!parts.length) return null;
    return Math.max.apply(null, parts);
  }

  // Mirrors normalizeSkuList() in the PDP app: strips "2x" quantity prefixes,
  // splits comma-separated component lists.
  function normalizeSkuList(raw) {
    if (!raw) return [];
    return String(raw).split(',')
      .map(function (s) { return s.trim().replace(/^\d+x/i, ''); })
      .filter(Boolean);
  }

  function maxShippingDaysForSkus(skus) {
    var list = window.scs_off_site_skus_full;
    if (!Array.isArray(list)) return null;
    var max = null;
    for (var i = 0; i < skus.length; i++) {
      var entry = list.filter(function (e) { return e && e.sku === skus[i]; })[0];
      if (entry && entry.shippingTime) {
        var days = parseShippingRangeMax(entry.shippingTime);
        if (days !== null && (max === null || days > max)) {
          max = days;
        }
      }
    }
    return max;
  }

  // Bundle components first (matches availability-check logic), fall back to
  // the single part number if no component SKU is found in the data set.
  function resolveMaxShippingDays(componentsCsv, fallbackSku) {
    var components = normalizeSkuList(componentsCsv);
    if (components.length) {
      var days = maxShippingDaysForSkus(components);
      if (days !== null) return days;
    }
    if (fallbackSku) {
      var days2 = maxShippingDaysForSkus([fallbackSku]);
      if (days2 !== null) return days2;
    }
    return null;
  }

  // Shared with other cart renderers (e.g. EV_CART_02_DRAWER.js, which builds
  // its own cart markup client-side and can't see the [data-scs-ship-by]
  // placeholders below).
  window.SCSShipBy = {
    resolveMaxShippingDays: resolveMaxShippingDays,
    formatShipByDate: formatShipByDate,
    addBusinessDays: addBusinessDays,
    getShipByText: function (color, componentsCsv, fallbackSku) {
      if (!window.scs_ship_by_date_format2 || !color) return null;
      var maxDays = resolveMaxShippingDays(componentsCsv, fallbackSku);
      if (maxDays === null) maxDays = DEFAULT_SHIP_BY_DAYS;
      if (maxDays >= MAX_FAST_SHIPPING_BUSINESS_DAYS) return null;
      return formatShipByDate(addBusinessDays(new Date(), maxDays));
    },
  };

  function updateShipByElements() {
    if (!window.scs_ship_by_date_format2) return;
    var els = document.querySelectorAll('[data-scs-ship-by]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var color = el.getAttribute('data-color');
      var components = el.getAttribute('data-components');
      var fallbackSku = el.getAttribute('data-fallback-sku');

      if (!color) {
        el.hidden = true;
        continue;
      }

      var maxDays = resolveMaxShippingDays(components, fallbackSku);
      if (maxDays === null) maxDays = DEFAULT_SHIP_BY_DAYS;

      if (maxDays >= MAX_FAST_SHIPPING_BUSINESS_DAYS) {
        el.hidden = true;
        continue;
      }

      var formatted = formatShipByDate(addBusinessDays(new Date(), maxDays));
      el.innerHTML = 'Your order will ship by <strong>' + formatted + '</strong>';
      el.hidden = false;
    }
  }

  var scheduled = null;
  function scheduleUpdate() {
    clearTimeout(scheduled);
    scheduled = setTimeout(updateShipByElements, 50);
  }

  var OBSERVE_SELECTORS = ['#CartDrawer-CartItems', '#main-cart-items'];
  var observed = [];
  function attachObservers() {
    OBSERVE_SELECTORS.forEach(function (selector) {
      var node = document.querySelector(selector);
      if (node && observed.indexOf(node) === -1) {
        observed.push(node);
        new MutationObserver(scheduleUpdate).observe(node, { childList: true, subtree: true });
      }
    });
  }

  document.addEventListener('cart:updated', scheduleUpdate);
  document.addEventListener('cart:refresh', scheduleUpdate);
  document.addEventListener('cart:added', scheduleUpdate);
  document.addEventListener('DOMContentLoaded', function () {
    attachObservers();
    updateShipByElements();
  });

  // Script tag is deferred, so DOMContentLoaded may already have fired.
  attachObservers();
  updateShipByElements();
  // Cart drawer/section markup can be injected slightly after initial load.
  setTimeout(attachObservers, 500);
  setTimeout(attachObservers, 2000);
})();
